import { useSearchParams } from 'react-router-dom';
import React, { useEffect, useMemo, useState } from "react";
import { ROUTES } from "constants/routes-path";
import {
    Button,
    Center,
    Divider,
    Flex,
    Grid,
    Stack,
    Text, useColorModeValue
} from "@chakra-ui/react";
import CardHeader from "components/Card/CardHeader";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { queryService } from 'services/queryService';
import { maskCPF } from "utils/forms-utils";
import {removeNonDigits} from "utils/utils";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import Loading from "components/Loading/Loading";
import { iso8601ToBrFormat } from "utils/utils";
import { FiSlash } from "react-icons/fi";


const QueriesDriversScoreHistoryResult = () => {
    const [searchParams] = useSearchParams();
    const [response, setResponse] = useState(undefined);
    const showToast = useCustomToast();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const cardBg = useColorModeValue("gray.50", "gray.700");
    const textColor = useColorModeValue("gray.700", "white");
    const translationKey = "queries";
    const formTranslationKey = `${translationKey}.form`;

    const ids = useMemo(
        () => ({
            driverName: "nome",
            driverCpf: "cpf",
            uf: "ufCnh",
            registerNumber: "nrRegistro",
            totalScore: "totalPontos",
            infractionList: "listaInfracoes"
        }),
        []
    );
    
    const fetchData = async () => {
        if (response) setResponse(undefined);

        try {
            const data = await queryService.findDriverScoreHistory(removeNonDigits(searchParams.get("valor")));
            setResponse(data);
        }
        catch (err) {
            showToast({ title: t(err), status: "error" });
            redirectToQueryDrivers();
        }
    };

    const redirectToQueryDrivers = () => {
        navigate(ROUTES.transportManagementPath + ROUTES.searches + ROUTES.searchDriversHistory)
    };

    useEffect(() => {
        fetchData();
    },[]);

    return (
        <Flex direction="column" pt={{ sm: "125px", lg: "75px" }}>
            <Card px="0px" mb="24px">
                <CardHeader px="22px" mb="24px">
                    <Flex direction="column">
                        <Text color={textColor} fontSize="lg" fontWeight="bold">
                            {t(`${translationKey}.title.query-drivers-score-history`)}
                        </Text>
                    </Flex>
                </CardHeader>
                <CardBody px="22px">
                    { response ? (
                        <>
                            <Card border="1px solid #3884cc" bg={cardBg} mb="24px">
                                <CardHeader>
                                    <Text color={textColor} fontSize="md" fontWeight="bold" >
                                        {t(`${formTranslationKey}.title.driver-data`)}
                                    </Text>
                                </CardHeader>
                                <Divider borderColor="#3884cc" mb="20px" />
                                <CardBody>
                                    <Stack spacing={4}>
                                        <Text color={textColor} fontSize="md">
                                            <Text as="span" fontWeight="bold">
                                                {t(`${formTranslationKey}.fields.driver-name.label`)}:
                                            </Text>{"\u00A0"}
                                            {response[ids.driverName] ?? "-"}
                                        </Text>
                                        <Grid templateColumns="repeat(4, 1fr)" gap="10px">
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                    {t(`${formTranslationKey}.fields.driver-cpf.label`)}:
                                                </Text>{"\u00A0"}
                                                {maskCPF(response[ids.driverCpf]) ?? "-"}
                                            </Text>
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                    {t(`${formTranslationKey}.fields.driver-registration-number.label`)}:
                                                </Text>{"\u00A0"}
                                                {response[ids.registerNumber] ?? "-"}
                                            </Text>
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                    {t(`${formTranslationKey}.fields.driver-score.label`)}:
                                                </Text>{"\u00A0"}
                                                {response[ids.totalScore] ?? "-"}
                                            </Text>
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                    {t(`${formTranslationKey}.fields.uf.label`)}:
                                                </Text>{"\u00A0"}
                                                {response[ids.uf] ?? "-"}
                                            </Text>
                                        </Grid>
                                    </Stack>
                                </CardBody>
                            </Card>
                            <Card border="1px solid #3884cc" bg={cardBg}>
                                <CardHeader>
                                    <Text color={textColor} fontSize="md" fontWeight="bold" >
                                        {t(`${formTranslationKey}.title.traffic-violation-list`)}
                                    </Text>
                                </CardHeader>
                                <Divider borderColor="#3884cc" mb="20px" />
                                <CardBody>
                                    {response[ids.infractionList] === undefined || response[ids.infractionList] === null || (Array.isArray(response[ids.infractionList]) && response[ids.infractionList].length === 0) ? (
                                        <Stack spacing={4}>
                                            <Text color={textColor}>
                                                <Center>
                                                    <FiSlash />{"\u00A0"}
                                                    {t(`${formTranslationKey}.fields.traffic-violation-empty-message.label`)}
                                                </Center>
                                            </Text>
                                        </Stack>
                                    ) :
                                        response[ids.infractionList].map((infractionItem) => (
                                        <Stack spacing={4}>
                                        <Text color={textColor} fontSize="md">
                                            <Text as="span" fontWeight="bold">
                                                {t(`${formTranslationKey}.fields.traffic-violation-description.label`)}:
                                            </Text>{"\u00A0"}
                                            {infractionItem.descricaoInfracao ?? "-"}
                                        </Text>
                                        <Grid templateColumns="repeat(3, 1fr)" gap="10px">
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                    {t(`${formTranslationKey}.fields.control-number.label`)}:
                                                </Text>{"\u00A0"}
                                                {infractionItem.nrControle ?? "-"}
                                            </Text>
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                    {t(`${formTranslationKey}.fields.traffic-violation-ait.label`)}:
                                                </Text>{"\u00A0"}
                                                {infractionItem.nrAutoinfr ?? "-"}
                                            </Text>
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                    {t(`${formTranslationKey}.fields.traffic-violation-date.label`)}:
                                                </Text>{"\u00A0"}
                                                {iso8601ToBrFormat(infractionItem.dtInfracao) ?? "-"}
                                            </Text>
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                    {t(`${formTranslationKey}.fields.traffic-violation-score.label`)}:
                                                </Text>{"\u00A0"}
                                                {infractionItem.nrPonto ?? "-"}
                                            </Text>
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                    {t(`${formTranslationKey}.fields.traffic-violation-nature.label`)}:
                                                </Text>{"\u00A0"}
                                                {infractionItem.natureza ?? "-"}
                                            </Text>
                                            <Text color={textColor} fontSize="md">
                                                <Text as="span" fontWeight="bold">
                                                    {t(`${formTranslationKey}.fields.traffic-violation-actuator.label`)}:
                                                </Text>{"\u00A0"}
                                                {infractionItem.orgaoAtuador ?? "-"}
                                            </Text>
                                        </Grid>
                                        <Divider  mb="24px" />
                                    </Stack>
                                    ))}
                                </CardBody>
                            </Card>
                        </>
                    ) : (
                        <Loading maxH={{ sm: "100px", md: "350px" }} />
                    )}
                    <Flex justifyContent="flex-start">
                        <Button
                            variant="light"
                            fontSize="sm"
                            alignSelf="flex-end"
                            mt="24px"
                            w="125px"
                            onClick={() => redirectToQueryDrivers()}
                        >
                            {t("system.actions.back")}
                        </Button>
                    </Flex>
                </CardBody>
            </Card>
        </Flex >
    );

};

export default QueriesDriversScoreHistoryResult;